import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Plus, Minus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";

const CART_KEY = "pp_cart";

export default function Shop() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("");
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem(CART_KEY) || "[]"));
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [method, setMethod] = useState("gcash");
  const [reference, setReference] = useState("");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api.get("/public/products", { params: category ? { category } : {} })
      .then((r) => setProducts(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [category]);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category))], [products]);
  const total = cart.reduce((s, c) => s + c.price * c.qty, 0);

  const add = (p) => {
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id);
      if (ex) return c.map((x) => x.product_id === p.id ? { ...x, qty: Math.min(x.qty + 1, p.stock) } : x);
      return [...c, { product_id: p.id, name: p.name, price: p.price, qty: 1, stock: p.stock, image_url: p.image_url }];
    });
    toast.success(`${p.name} added to cart`);
  };
  const setQty = (id, d) => setCart((c) => c.map((x) => x.product_id === id ? { ...x, qty: Math.max(1, Math.min(x.qty + d, x.stock)) } : x));
  const remove = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  const checkout = async () => {
    if (!user) {
      toast.info("Please sign in to place an order");
      navigate("/login?next=/shop");
      return;
    }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", {
        items: cart.map((c) => ({ product_id: c.product_id, qty: c.qty, variant: c.variant || null })),
        payments: [{ method, amount: total, reference: method === "gcash" ? reference : null }],
      });
      toast.success(`Order ${data.receipt_no} placed! Pay ${method === "gcash" ? "via GCash" : "cash"} at pickup.`);
      setCart([]);
      setCheckoutOpen(false);
      setCartOpen(false);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Pet Supply Shop</h1>
          <p className="text-sm text-slate-500 mt-1">Order online, pay &amp; pick up in store. Stock syncs live with our POS.</p>
        </div>
        <Button className="bg-[#0E7C84] hover:bg-[#093F46] relative" onClick={() => setCartOpen(true)} data-testid="cart-open-btn">
          <ShoppingCart className="w-4 h-4 mr-2" /> Cart
          {cart.length > 0 && <span className="absolute -top-2 -right-2 bg-[#E84855] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{cart.length}</span>}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setCategory("")} data-testid="cat-all"
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${!category ? "bg-[#0E7C84] text-white" : "bg-white border border-[#BFE8E9] hover:border-[#0E7C84]"}`}>
          All
        </button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c.toLowerCase().replace(/ /g, "-")}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${category === c ? "bg-[#0E7C84] text-white" : "bg-white border border-[#BFE8E9] hover:border-[#0E7C84]"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((p, i) => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#BFE8E9] overflow-hidden hover-lift animate-rise" style={{ animationDelay: `${(i % 8) * 50}ms` }} data-testid={`product-card-${p.sku}`}>
            <div className="h-36 bg-[#E2F5F5] overflow-hidden">
              {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <Badge variant="secondary" className="text-[10px] mb-1.5">{p.category}</Badge>
              <p className="font-semibold text-sm leading-snug min-h-[2.5rem]">{p.name}</p>
              <p className="text-xs text-slate-400 font-mono">{p.sku}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="font-display font-bold text-[#0E7C84]">{peso(p.price)}</span>
                <Button size="sm" className="bg-[#E84855] hover:bg-[#C62836] h-8" onClick={() => add(p)} data-testid={`add-to-cart-${p.sku}`}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[11px] mt-1 text-slate-500">{p.stock <= p.low_stock_threshold ? <span className="text-amber-600 font-medium">Only {p.stock} left</span> : `${p.stock} in stock`}</p>
            </div>
          </div>
        ))}
      </div>

      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
          <SheetHeader><SheetTitle className="font-display">Your Cart</SheetTitle></SheetHeader>
          <div className="flex-1 overflow-y-auto space-y-3 py-4">
            {cart.length === 0 && <p className="text-sm text-slate-500 text-center py-10">Your cart is empty.</p>}
            {cart.map((c) => (
              <div key={c.product_id} className="flex items-center gap-3 bg-[#F2FBFB] rounded-xl p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium leading-snug">{c.name}</p>
                  <p className="text-xs text-slate-500">{peso(c.price)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button className="w-6 h-6 rounded-md bg-white border flex items-center justify-center" onClick={() => setQty(c.product_id, -1)} data-testid={`cart-dec-${c.product_id.slice(0, 6)}`}><Minus className="w-3 h-3" /></button>
                  <span className="w-6 text-center text-sm font-medium">{c.qty}</span>
                  <button className="w-6 h-6 rounded-md bg-white border flex items-center justify-center" onClick={() => setQty(c.product_id, 1)} data-testid={`cart-inc-${c.product_id.slice(0, 6)}`}><Plus className="w-3 h-3" /></button>
                </div>
                <button className="text-slate-400 hover:text-red-500" onClick={() => remove(c.product_id)} data-testid={`cart-remove-${c.product_id.slice(0, 6)}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          {cart.length > 0 && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex justify-between font-display font-bold text-lg"><span>Total</span><span data-testid="cart-total">{peso(total)}</span></div>
              <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => setCheckoutOpen(true)} data-testid="checkout-btn">Checkout</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent data-testid="checkout-dialog">
          <DialogHeader><DialogTitle className="font-display">Checkout — {peso(total)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Payment method</Label>
              <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-2 gap-2">
                <label className={`flex items-center gap-2 border rounded-xl p-3 cursor-pointer ${method === "gcash" ? "border-[#0052FF] bg-blue-50" : ""}`} data-testid="pay-gcash">
                  <RadioGroupItem value="gcash" /> <span className="text-sm font-semibold text-[#0052FF]">GCash</span>
                </label>
                <label className={`flex items-center gap-2 border rounded-xl p-3 cursor-pointer ${method === "cash" ? "border-[#0E7C84] bg-[#D9F7F8]" : ""}`} data-testid="pay-cash">
                  <RadioGroupItem value="cash" /> <span className="text-sm font-semibold">Cash on Pickup</span>
                </label>
              </RadioGroup>
            </div>
            {method === "gcash" && (
              <div>
                <Label>GCash reference number</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. 009812345678" data-testid="gcash-reference" />
              </div>
            )}
            <div className="bg-[#E2F5F5] rounded-xl p-3 text-sm space-y-1">
              {cart.map((c) => <div key={c.product_id} className="flex justify-between"><span>{c.name} x{c.qty}</span><span>{peso(c.price * c.qty)}</span></div>)}
              <div className="flex justify-between font-bold border-t border-[#BFE8E9] pt-1 mt-1"><span>Total</span><span>{peso(total)}</span></div>
            </div>
            <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46]" onClick={checkout} disabled={placing} data-testid="place-order-btn">
              {placing ? "Placing order…" : "Place Order"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
